import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Map, Refresh, TrendingUp, Groups, ArrowDownward, ArrowUpward } from '@mui/icons-material';

const formatInr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const ZoneDriverPricing = () => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('/admin/zones/driver-pricing-stats');
      if (data.success) {
        setStats(data.stats || []);
        setLastUpdated(new Date());
      } else {
        setError(data.message || 'Failed to load statistics');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load zone pricing statistics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 60000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  if (loading && stats.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress sx={{ color: '#116611' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Map sx={{ color: '#116611' }} />
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
                Zone Driver Pricing
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
              Live statistics from driver-entered base price and price per 1000 litres, grouped by service zone.
            </Typography>
            {lastUpdated && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Last refreshed: {lastUpdated.toLocaleTimeString('en-IN')}
              </Typography>
            )}
          </Box>
          <Tooltip title="Refresh now">
            <IconButton onClick={fetchStats} disabled={loading} sx={{ bgcolor: 'rgba(17,102,17,0.08)' }}>
              <Refresh sx={{ color: '#116611' }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 800 }}>Zone Name</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Location</TableCell>
              <TableCell align="center" sx={{ fontWeight: 800 }}>
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                  <Groups fontSize="small" /> Drivers
                </Stack>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, color: '#1565c0' }}>
                <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5}>
                  <TrendingUp fontSize="small" /> Avg Base
                </Stack>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, color: '#6a1b9a' }}>
                Avg / 1000L
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, color: '#2e7d32' }}>
                <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5}>
                  <ArrowDownward fontSize="small" /> Base Min
                </Stack>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, color: '#c62828' }}>
                <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5}>
                  <ArrowUpward fontSize="small" /> Base Max
                </Stack>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, color: '#2e7d32' }}>/1000L Min</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, color: '#c62828' }}>/1000L Max</TableCell>
              <TableCell align="center" sx={{ fontWeight: 800 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stats.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 6, color: '#64748b' }}>
                  No zones configured yet. Add zones in Zone Management.
                </TableCell>
              </TableRow>
            ) : (
              stats.map((row) => (
                <TableRow key={row.zoneId} hover>
                  <TableCell>
                    <Typography sx={{ fontWeight: 700 }}>{row.zoneName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {[row.city, row.state].filter(Boolean).join(', ') || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={row.totalDrivers}
                      size="small"
                      color={row.totalDrivers > 0 ? 'primary' : 'default'}
                      sx={{ fontWeight: 700, minWidth: 40 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontWeight: 800, color: '#1565c0', fontSize: '1.05rem' }}>
                      {row.averageBasePrice > 0 ? formatInr(row.averageBasePrice) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontWeight: 800, color: '#6a1b9a', fontSize: '1.05rem' }}>
                      {row.averagePricePer1000 > 0 ? formatInr(row.averagePricePer1000) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontWeight: 700, color: '#2e7d32' }}>
                      {row.minBasePrice > 0 ? formatInr(row.minBasePrice) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontWeight: 700, color: '#c62828' }}>
                      {row.maxBasePrice > 0 ? formatInr(row.maxBasePrice) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontWeight: 700, color: '#2e7d32' }}>
                      {row.minPricePer1000 > 0 ? formatInr(row.minPricePer1000) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontWeight: 700, color: '#c62828' }}>
                      {row.maxPricePer1000 > 0 ? formatInr(row.maxPricePer1000) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={row.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={row.isActive ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Alert severity="info" sx={{ mt: 3 }}>
        Averages are calculated from each driver's latest registered vehicle price in that zone. Base price and
        price per 1000 litres come from the driver app's Price Details form.
      </Alert>
    </Box>
  );
};

export default ZoneDriverPricing;
